import { useAuth } from '@/contexts'
import { useUserProfile, useUpdateUserProfile } from '@/hooks/useUserProfile'
import { UserProfileForm } from '@/components/settings/profile'
import type { UserProfileFormValues } from '@/components/settings/profile'
import { Skeleton } from '@/components/ui/skeleton'

export function UserProfilePage() {
  const { user } = useAuth()
  const { data: profile, isLoading: isProfileLoading } = useUserProfile()
  const updateMutation = useUpdateUserProfile()

  const handleSubmit = async (data: UserProfileFormValues) => {
    if (!profile?.id) return

    await updateMutation.mutateAsync({
      id: profile.id,
      data,
    })
  }

  if (isProfileLoading) {
    return (
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>

        {/* Personal Info section skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
          <Skeleton className="h-10 w-full" />
        </div>

        {/* Notifications section skeleton */}
        <div className="space-y-4 border-t pt-8">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profile Settings</h1>
        <p className="text-muted-foreground">
          Manage your account information and notification preferences
        </p>
      </div>

      {/* User email info */}
      {user?.email && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">Email Address</p>
          <p className="font-medium">{user.email}</p>
        </div>
      )}

      {/* Profile Form */}
      {profile ? (
        <UserProfileForm
          profile={profile}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
        />
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Unable to load your profile. Please refresh the page and try again.
          </p>
        </div>
      )}
    </div>
  )
}
